/**
 * Client-side platform detection using Capacitor and Electron.
 *
 * Returns "ios_app", "android_app", "desktop_app", or "web" depending on the runtime.
 * Safe to call on server (returns "web") and in browsers without Capacitor/Electron.
 */

import { reportWarning } from "@/lib/observability/report-error";

let cachedPlatform: string | null = null;

export function getClientPlatform(): string {
  if (cachedPlatform) return cachedPlatform;

  if (typeof window === "undefined") {
    return "web";
  }

  try {
    // Electron: preload script injects electronAPI on the window object
    if (window.electronAPI !== undefined) {
      cachedPlatform = "desktop_app";
      return cachedPlatform;
    }

    // Capacitor injects itself on the window object in native apps
    // biome-ignore lint/suspicious/noExplicitAny: replaces eslint-disable for @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor;
    if (
      cap &&
      typeof cap.isNativePlatform === "function" &&
      cap.isNativePlatform()
    ) {
      const platform = cap.getPlatform?.() as string | undefined;
      cachedPlatform =
        platform === "ios" || platform === "android"
          ? `${platform}_app`
          : "web";
    } else {
      cachedPlatform = "web";
    }
  } catch (err) {
    console.error("[platform-context] Failed to detect platform:", err);
    reportWarning(err, {
      source: "lib.analytics.platform-context",
      feature: "platform",
      action: "getClientPlatform",
    });
    cachedPlatform = "web";
  }

  return cachedPlatform;
}

/**
 * Returns structured platform context for Amplitude user properties.
 * Combines Capacitor detection with user-agent parsing.
 */
export function getClientPlatformContext(): {
  platform: string;
  os: string;
  browser: string;
  device_type: string;
} {
  // Dynamic import avoidance — use the same UA parsing as observability
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : "unknown";

  return {
    platform: getClientPlatform(),
    os: inferOsFromUA(userAgent),
    browser: inferBrowserFromUA(userAgent),
    device_type: inferDeviceFromUA(userAgent),
  };
}

// Inline UA parsing to avoid circular dependency with observability module.
// These mirror the functions in lib/observability/request-context.ts.
function inferOsFromUA(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod"))
    return "iOS";
  if (ua.includes("android")) return "Android";
  if (ua.includes("cros")) return "ChromeOS";
  if (ua.includes("windows")) return "Windows";
  if (ua.includes("mac os x") || ua.includes("macintosh")) return "macOS";
  if (ua.includes("linux")) return "Linux";
  return "unknown";
}

function inferBrowserFromUA(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg/")) return "Edge";
  if (ua.includes("opr/") || ua.includes("opera")) return "Opera";
  if (ua.includes("chrome/") && !ua.includes("edg/")) return "Chrome";
  if (ua.includes("safari/") && !ua.includes("chrome/")) return "Safari";
  if (ua.includes("firefox/")) return "Firefox";
  if (ua.includes("msie") || ua.includes("trident/")) return "IE";
  return "unknown";
}

function inferDeviceFromUA(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes("ipad") || ua.includes("tablet")) return "tablet";
  if (ua.includes("mobile") || ua.includes("iphone") || ua.includes("android"))
    return "mobile";
  return "desktop";
}
